import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState } from "react";
import { toast } from "sonner";
import type { ApiUser } from "@shared/types/models";

export const useUsers = () => {
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    users: boolean;
    saving: boolean;
    deleting: boolean;
  }>({
    users: false,
    saving: false,
    deleting: false,
  });

  const loadAllUsers = async (
    setUsers: (users: ApiUser[]) => void,
  ): Promise<void> => {
    try {
      setLoading((prev) => ({ ...prev, users: true }));
      const response = await api(
        "/auth/users?shape=all",
        { method: "GET" },
        dispatch,
      );
      setUsers(response.users ?? []);
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to load users");
    } finally {
      setLoading((prev) => ({ ...prev, users: false }));
    }
  };

  const updateUser = async (
    userId: string,
    patch: { name: string; gradYear: number | null },
  ): Promise<ApiUser | null> => {
    try {
      setLoading((prev) => ({ ...prev, saving: true }));
      const response = await api(
        `/auth/users/${userId}`,
        { method: "PUT", body: patch },
        dispatch,
      );
      toast.success("User updated");
      return response.user as ApiUser;
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to update user");
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteUser = async (userId: string): Promise<boolean> => {
    try {
      setLoading((prev) => ({ ...prev, deleting: true }));
      await api(
        `/auth/users/${userId}`,
        { method: "DELETE" },
        dispatch,
      );
      toast.success("User deleted");
      return true;
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete user");
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, deleting: false }));
    }
  };

  return { loading, loadAllUsers, updateUser, deleteUser };
};
